import {Injectable} from '@angular/core';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {BimModel} from '../bim-viewer/src/lib/models/bim.model';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BimSection, BimSectionPlan} from '../bp-common/models/plan';
import {isPlanCalibrated} from './mm-bim-view/helpers/helpers';
import {MmFilterService} from '../mm-filter/mm-filter.service';
import {Structure} from '@docma-mm-core/structure/structure';
import {filter, map, switchMap} from 'rxjs/operators';
import {MmProjectLoaderService} from '../mm-project/mm-project-loader.service';
import {BpApiService} from '../bp-common/bp-api.service';
import {MmPlansManagerService} from '../mm-faults/mm-plans-view/services/mm-plans-manager.service';
import {Plan} from '@docma-mm-core/plan/plan';
import {MmProjectService} from '../mm-project/mm-project.service';
import {Project} from '@docma-mm-core/project/project';
import {PersonalizationService} from '@docma-mm-core/personalization/personalization.service';
import {MmFaultsSplitService} from '../mm-faults/mm-faults-view/services/mm-faults-split.service';
import {MmFaultsViewService} from '../mm-faults/mm-faults-view/services/mm-faults-view.service';


@UntilDestroy()
@Injectable()
export class MmBimService {

  mmPlanManager: MmPlansManagerService;

  public sectionsByPlanId: Map<string, BimSection>;
  public sectionPlansByPlanId: Map<string, BimSectionPlan>;
  private defaultBimModelSubject = new BehaviorSubject<BimModel>(null);
  public defaultBimModel$ = this.defaultBimModelSubject.asObservable();
  private sectionsSubject = new BehaviorSubject<BimSection[]>([]);
  public sections$ = this.sectionsSubject.asObservable();
  private sectionsPlansSubject = new BehaviorSubject<BimSectionPlan[]>(null);
  private plansSubject = new BehaviorSubject<Plan[]>([]);
  public bimPlans$ = this.plansSubject.asObservable();
  private project: Project;
  private selectedPlanSubject = new BehaviorSubject<Plan>(null);
  public selectedPlan$ = this.selectedPlanSubject.asObservable();
  private isBimViewOpen: boolean = false;

  public set IsBimViewOpen(value: boolean) {
    this.isBimViewOpen = value;
  }

  constructor(private projectService: MmProjectService,
              private bpApiService: BpApiService,
              private readonly mmPersonalizationService: PersonalizationService,
              private readonly filterService: MmFilterService,
              private readonly loaderService: MmProjectLoaderService,
              readonly faultsSplitService: MmFaultsSplitService) {

  }

  attachTo(targetToAttach: any) {
    if (targetToAttach instanceof MmPlansManagerService) {
      this.mmPlanManager = targetToAttach;
    }

    this.projectService.currentProject.pipe(untilDestroyed(this))
      .subscribe(async project => {
        this.reset();
        this.project = project;
        await this.setDefaultModel();
        await this.setSections();
      });

    this.sectionsPlansSubject.pipe(
      switchMap(bimPlans => this.mmPlanManager.plansByStructure$.pipe(map(mmPlans => ({bimPlans, mmPlans})))),
      untilDestroyed(this)
    ).subscribe(r => {
      r.bimPlans = r.bimPlans || [];
      const mmBimPlans = (r.mmPlans || []).filter(mmPlan => this.bimPlans.find(bp => mmPlan.id.toString() === bp.planId));
      this.plansSubject.next(mmBimPlans);
    })

    this.plansSubject
      .pipe(
        switchMap(plans => this.mmPlanManager.selectedPlan$.pipe(
          filter(sp => !sp || (sp?.id !== this.selectedPlan?.id && this.faultsSplitService.toggledRightViews.length > 0 && this.faultsSplitService.toggledRightViews[0]  == 'planView')),
          map(selectedPlan => ({plans, selectedPlan})))
        ),
        untilDestroyed(this)
      )
      .subscribe(r => {

        const plansCount = (r.plans || []).length;
        if (plansCount === 0) {
          this.selectPlan(null);
          return;
        } else if (plansCount === 1 && this.isBimViewOpen) {
          r.selectedPlan = r.plans[0];
        }

        if (r.selectedPlan) {
          // select plan from mm plan manager
          if ((this.plansSubject.value || []).find(p => p.id === r.selectedPlan.id)) {
            this.selectPlan(r.selectedPlan);
          } else {
            r.selectedPlan = null;
            this.selectPlan(null);
          }
        } else {
          this.selectPlan(null);
        }
      });
  }

  get selectedPlan(): Plan {

    return this.selectedPlanSubject.value;
  }

  get sections(): BimSection[] {
    return this.sectionsSubject.value;
  }

  get bimPlans(): BimSectionPlan[] {
    return this.sectionsPlansSubject.value;
  }

  get defaultBimModel(): BimModel {
    return this.defaultBimModelSubject.value;
  }

  public selectPlan(plan: Plan) {
    if (!plan || this.plansSubject.value.find(p => p?.id === plan?.id)) {
      this.saveLastPlan(plan);
      this.selectedPlanSubject.next(plan);
      if (plan) {
        this.mmPlanManager.setSelectedPlan(plan);
      }
      return;
    }
    throw new Error(`Can't select plan with id ${plan?.id}`)
  }

  getSectionByPlanId(planId: string): BimSection {
    return this.sectionsByPlanId.get(planId);
  }

  getSectionPlan(planId: string): BimSectionPlan {
    return this.sectionPlansByPlanId.get(planId);
  }

  public resetState() {
    this.defaultBimModelSubject.next(null);
    this.sectionsSubject.next(null);
  }

  public reset(): void {
    this.defaultBimModelSubject.next(null);
    this.sectionsSubject.next([]);
    this.sectionsPlansSubject.next([]);
    this.sectionsByPlanId = new Map<string, BimSection>();
    this.sectionPlansByPlanId = new Map<string, BimSectionPlan>();

  }

  private async selectLastPlan() {
    // const lastBimPlanId = await this.mmPersonalizationService.getProjectValue(this.project?.MyDocmaUid, PersonalizationKeysEnum.LastBimPlanId);
    // if (lastBimPlanId) {
    //   const foundPlan = (this.plansSubject.value || []).find(x => x?.id === lastBimPlanId);
    //   if (foundPlan) {
    //     this.selectPlan(foundPlan);
    //   }
    // }
  }

  private async saveLastPlan(plan: Plan) {
    // if (plan?.id) {
    //   await this.mmPersonalizationService.setProjectValue(this.project?.MyDocmaUid, PersonalizationKeysEnum.LastBimPlanId, plan.id);
    // }
  }

  private async setSections(): Promise<void> {

    try {
      const modelId = this.defaultBimModelSubject.value?.id;
      if (!modelId) {
        return;
      }

      const sections = await firstValueFrom(this.bpApiService.getBimSections(this.defaultBimModelSubject.value?.id)) as BimSection[];
      const bimPlans = sections.reduce(
        (arr, section) => {

          return arr.concat(section?.plans.filter(bimPlan => {
            if (isPlanCalibrated(bimPlan)) {
              this.sectionsByPlanId.set(bimPlan.planId, section);
              this.sectionPlansByPlanId.set(bimPlan.planId, bimPlan);
              return true;
            }
            return false;
          }) || []);
        }
        , []) as BimSectionPlan[];

      this.sectionsSubject.next(sections);
      this.sectionsPlansSubject.next(bimPlans);


    } catch (e) {
      console.error(`an error: ${e} occurred while getting floor plans`);
      this.reset();
    }
  }

  private async setDefaultModel(): Promise<void> {
    try {
      const models = await firstValueFrom(this.bpApiService.getDefaultModel()) as BimModel[];
      const defaultModel = models.find(bimModel => bimModel?.default);
      this.defaultBimModelSubject.next(defaultModel);
    } catch (e) {
      console.error(`an error: ${e} occurred while getting default model`);
    }


  }

}


export function getChildBimPlansOfStructure(selectedStructure: Structure, planList: BimSectionPlan[]): BimSectionPlan[] {
  let structures = [];
  if (selectedStructure.getFlatSortedStructures) {
    structures = selectedStructure.getFlatSortedStructures();
  }
  let childPlans = [];
  structures.forEach(struct => {
    const plans = planList.filter(plan => plan.plan.structureId === struct.Id);
    childPlans = [...childPlans, ...plans];
  });

  return childPlans;
}
